import { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import clsx from "clsx";
import {
  LayoutDashboard, Mail, Users, Zap, BarChart3, Settings,
  LogOut, Palette, ShieldCheck, Store, Target, Plug,
  Shield, Menu, X, ChevronRight, Heart, Sparkles, FlaskConical, Eye, Layout,
  Inbox,
} from "lucide-react";

const NAV_GROUPS = [
  {
    label: "Core",
    items: [
      { to: "/dashboard",        icon: LayoutDashboard, label: "Dashboard"     },
      { to: "/inbox",             icon: Inbox,           label: "Inbox"         },
      { to: "/campaigns",        icon: Mail,            label: "Campaigns"     },
      { to: "/subscribers",      icon: Users,           label: "Subscribers"   },
      { to: "/templates/editor", icon: Palette,         label: "Templates"     },
    ],
  },
  {
    label: "Growth",
    items: [
      { to: "/automations",    icon: Zap,         label: "Automations"   },
      { to: "/leads",          icon: Target,      label: "Lead Finder"   },
      { to: "/landing-pages",  icon: Layout,      label: "Landing Pages" },
      { to: "/integrations",   icon: Plug,        label: "Integrations"  },
      { to: "/network",        icon: Store,       label: "Network"       },
    ],
  },
  {
    label: "Intelligence",
    items: [
      { to: "/analytics",       icon: BarChart3,   label: "Analytics"     },
      { to: "/deliverability",  icon: ShieldCheck, label: "Deliverability"},
      { to: "/email-health",    icon: Heart,       label: "Email Health"  },
      { to: "/recommendations", icon: Sparkles,    label: "Insights"      },
      { to: "/ab-tests",        icon: FlaskConical, label: "A/B Tests"     },
      { to: "/competitor-tracker", icon: Eye,         label: "Competitors"   },
    ],
  },
];

// Bottom nav items for mobile (most-used only)
const MOBILE_NAV = [
  { to: "/dashboard",    icon: LayoutDashboard, label: "Home"       },
  { to: "/inbox",         icon: Inbox,           label: "Inbox"      },
  { to: "/campaigns",    icon: Mail,            label: "Campaigns"  },
  { to: "/analytics",    icon: BarChart3,       label: "Analytics"  },
  { to: "/settings",     icon: Settings,        label: "Settings"   },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const firstName = user?.name?.split(" ")[0] || "there";
  const initials  = user?.name?.split(" ").map((n: string) => n[0]).join("").slice(0, 2).toUpperCase() || "MM";

  return (
    <div className="flex h-screen bg-paper overflow-hidden">

      {/* ── Mobile overlay ── */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-ink/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* ── Sidebar ── */}
      <aside
        className={clsx(
          "fixed inset-y-0 left-0 z-50 w-64 bg-ink flex flex-col transition-transform duration-300 lg:relative lg:translate-x-0 lg:flex-shrink-0",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {/* Logo */}
        <div className="flex items-center justify-between px-5 pt-6 pb-5">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-coral rounded-lg flex items-center justify-center text-white font-display font-bold text-sm -rotate-2 shadow-lg shadow-coral/40">
              M
            </div>
            <span className="text-white font-display font-bold text-xl tracking-tight">MoreMail</span>
          </div>
          <button
            onClick={() => setMobileOpen(false)}
            className="lg:hidden p-1.5 rounded-lg text-white/40 hover:text-white/70 hover:bg-white/[0.06]"
          >
            <X size={18} />
          </button>
        </div>

        {/* Nav groups */}
        <nav className="flex-1 overflow-y-auto px-3 pb-4 space-y-5">
          {NAV_GROUPS.map((group) => (
            <div key={group.label}>
              <p className="px-3 mb-1.5 text-[10px] font-bold uppercase tracking-widest text-white/25">
                {group.label}
              </p>
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      clsx(
                        "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150",
                        isActive
                          ? "bg-coral/15 text-coral shadow-sm"
                          : "text-white/50 hover:text-white/80 hover:bg-white/[0.05]"
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <item.icon size={16} className={isActive ? "text-coral" : ""} />
                        <span>{item.label}</span>
                        {isActive && (
                          <ChevronRight size={14} className="ml-auto text-coral/60" />
                        )}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Plan bar */}
        <div className="mx-3 mb-3 p-3.5 rounded-xl bg-white/[0.04] border border-white/[0.06]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] text-white/40 font-semibold uppercase tracking-wider">
              {user?.plan || "Starter"} Plan
            </span>
            <NavLink
              to="/settings"
              className="text-[11px] text-coral/70 hover:text-coral font-semibold transition-colors"
            >
              Upgrade
            </NavLink>
          </div>
          <div className="h-1 bg-white/[0.08] rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-coral to-coral/70 rounded-full transition-all duration-700"
              style={{ width: user?.plan === "growth" ? "50%" : user?.plan === "enterprise" ? "90%" : "22%" }}
            />
          </div>
        </div>

        {/* User row */}
        <div className="px-3 pb-4 space-y-0.5">
          {user?.is_admin && (
            <button
              onClick={() => { navigate("/admin"); setMobileOpen(false); }}
              className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-red-400/70 hover:text-red-400 hover:bg-red-500/[0.08] text-sm font-medium transition-colors"
            >
              <Shield size={16} />
              Admin Panel
            </button>
          )}
          <div className="flex items-center gap-3 px-3 py-2.5">
            <div className="w-7 h-7 rounded-full bg-coral/20 border border-coral/30 flex items-center justify-center text-coral text-[11px] font-bold flex-shrink-0">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white/80 text-xs font-semibold truncate">{firstName}</p>
              <p className="text-white/30 text-[10px] truncate">{user?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-1.5 rounded-lg text-white/30 hover:text-white/60 hover:bg-white/[0.06] transition-colors"
              title="Log out"
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </aside>

      {/* ── Main area ── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Mobile topbar */}
        <div className="lg:hidden flex items-center justify-between px-4 py-3 bg-ink border-b border-white/[0.06] flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 bg-coral rounded-lg flex items-center justify-center text-white font-display font-bold text-xs -rotate-2">
              M
            </div>
            <span className="text-white font-display font-bold text-lg tracking-tight">MoreMail</span>
          </div>
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/[0.08]"
          >
            <Menu size={20} />
          </button>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto pb-20 lg:pb-0">
          <Outlet />
        </main>

        {/* Mobile bottom tab bar */}
        <div className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-ink/95 backdrop-blur-md border-t border-white/[0.08] safe-area-pb">
          <div className="flex items-center justify-around px-2 py-2">
            {MOBILE_NAV.map((item) => {
              const isActive = location.pathname === item.to ||
                (item.to !== "/dashboard" && location.pathname.startsWith(item.to));
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className="flex flex-col items-center gap-1 px-3 py-1.5 rounded-xl transition-all"
                >
                  <item.icon
                    size={20}
                    className={clsx(
                      "transition-colors",
                      isActive ? "text-coral" : "text-white/40"
                    )}
                  />
                  <span className={clsx(
                    "text-[10px] font-semibold tracking-wide transition-colors",
                    isActive ? "text-coral" : "text-white/30"
                  )}>
                    {item.label}
                  </span>
                </NavLink>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
